<!--
  ActionButton — the booking panel's primary action.

  Replaces `v-loading` on <el-button>. That directive is Element Plus's
  *container* loader: it paints a grey mask ON TOP of the element, so on a
  button it covered the label with a translucent block and a spinner. The
  `:deep(.ep-loading-mask)` overrides that used to live in NewRequest were an
  attempt to make that mask tolerable, which is the tell that it was the wrong
  tool.

  Three states, because the two actions on this panel are genuinely different
  shapes of wait:

    idle     — the label, clickable
    loading  — inline spinner, a different label, and an indeterminate sweep
               along the bottom edge
    done     — a drawn check and a success label, held briefly so the outcome
               registers before whatever happens next

  The sweep is deliberately INDETERMINATE. The server reports no progress for
  either action, so a percentage or named stages ("checking distance…") would
  be invented — it would look more informative while telling the user
  something we do not know. Motion conveys "working"; the label carries the
  only claim we can actually make.

  Styles are self-contained rather than inherited from the parent's
  `.new-request-full-button`. A parent's scoped CSS only reaches a child's
  root element, and relying on that is what broke the address inputs when
  they gained a wrapper.

  NOTE ON COLOUR: this button renders on `.new-request-body-section`, which is
  `#f8fafc` — near-white. It is NOT on the navy panel header. The first
  version used white text on `rgba(255,255,255,.14)`, which is invisible
  there; it was previewed against the navy header by mistake. Solid navy with
  white text is both visible and what the button looked like before.
-->
<script lang="ts" setup>
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  label: string
  loadingLabel?: string
  doneLabel?: string
  state?: 'idle' | 'loading' | 'done'
  disabled?: boolean
}>(), { loadingLabel: 'Working…', doneLabel: 'Done', state: 'idle', disabled: false })

const emit = defineEmits<{ (e: 'click'): void }>()

const busy = computed(() => props.state === 'loading')
const done = computed(() => props.state === 'done')

const text = computed(() => (
  busy.value ? props.loadingLabel : done.value ? props.doneLabel : props.label
))

function onClick() {
  if (props.disabled || busy.value || done.value) return
  emit('click')
}
</script>

<template>
  <button
    type="button"
    class="axn"
    :class="{ 'is-busy': busy, 'is-done': done }"
    :disabled="disabled || busy || done"
    :aria-busy="busy"
    @click="onClick"
  >
    <span class="axn__face">
      <!-- Spinner and tick are siblings of the label rather than replacing
           it, so the button's width does not jump between states. -->
      <span v-if="busy" class="axn__spinner" aria-hidden="true"></span>
      <svg v-else-if="done" class="axn__tick" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M4 10.5 L8 14.5 L16 6" />
      </svg>
      <!-- aria-live so a screen reader hears the state change without the
           button losing focus. -->
      <span class="axn__label" aria-live="polite">{{ text }}</span>
    </span>

    <span v-if="busy" class="axn__sweep" aria-hidden="true"></span>
  </button>
</template>

<style scoped>
.axn {
  position: relative;
  width: 100%;
  height: 42px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  /* Solid navy: this sits on the panel's near-white body (#f8fafc). */
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  color: #ffffff;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.2s ease, color 0.2s ease;
}
.axn:hover:not(:disabled) { background: linear-gradient(135deg, #1a3560 0%, #2a4f8c 100%); }
.axn:focus-visible {
  outline: 2px solid #2d43ff;
  outline-offset: 2px;
}
.axn:disabled { cursor: default; }
/* Busy is not "unavailable" — keep it fully legible rather than dimming it to
   the usual disabled grey, since the label is the only progress signal. */
.axn.is-busy { opacity: 1; }
.axn:disabled:not(.is-busy):not(.is-done) { opacity: 0.42; }

.axn.is-done {
  background: #16803c;
  color: #ffffff;
}

.axn__face {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.axn__spinner {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.34);
  border-top-color: #ffffff;
  animation: axn-spin 0.6s linear infinite;
}

.axn__tick {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  fill: none;
  stroke: #ffffff;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  /* Drawn rather than popped in — the line tracing reads as confirmation
     completing, not as an icon appearing. */
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation: axn-draw 0.32s ease-out forwards;
}

/* Indeterminate sweep along the bottom edge. Deliberately not a percentage:
   nothing here reports real progress. */
.axn__sweep {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
  animation: axn-sweep 1.15s ease-in-out infinite;
}

@keyframes axn-spin { to { transform: rotate(360deg); } }
@keyframes axn-draw { to { stroke-dashoffset: 0; } }
@keyframes axn-sweep {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

@media (prefers-reduced-motion: reduce) {
  .axn__spinner { animation-duration: 1.4s; }
  .axn__sweep { animation: none; opacity: 0.55; transform: translateX(75%); }
  .axn__tick { animation: none; stroke-dashoffset: 0; }
}
</style>
